import { FPS } from 'creation/src/creation-tools/constants'
import { giphyUrl } from '@/app/util/env'
import { getCookie } from '@/util/url'

const REMOVE_BACKGROUND_PATH = '/ajax/gif/remove_background'
const MAX_ENCODED_CHARS_PER_REQUEST = 1_500_000

/**
 * What actually bounds this request is the FIELD COUNT, not the payload size
 * and not a model deadline. Each frame is sent as its own `frames[]` field, and
 * Django's `DATA_UPLOAD_MAX_NUMBER_FIELDS` is 1000 (app/settings/base.py:272).
 * One field past it, `TooManyFieldsSent` surfaces as a **405 Method Not
 * Allowed** — a status that looks like a routing or method problem and sent
 * this investigation down three wrong paths.
 *
 * Measured against production, through the local dev proxy:
 *
 *     1000 fields x 4 bytes   -> 403 (reaches the view; CSRF refuses it)
 *     1001 fields x 4 bytes   -> 405, instantly
 *        1 field  x 10 MB     -> 403
 *
 * So size is not the constraint: a 10MB single-field body sails through, while
 * 1001 four-byte fields do not. It is also not a timeout — the 1001-field probe
 * returned immediately and never reached Seldon, and a gRPC failure would
 * surface as HTTP 200 with `frames: []` anyway (views.py catches GrpcException),
 * not as a 405.
 *
 * A third of a second per request keeps each call to 11 fields, two orders of
 * magnitude under the cap and independent of how long the video is. The encoded
 * character cap is belt-and-braces for unusually large frames; nothing measured
 * requires it.
 */
const FRAMES_PER_REQUEST = Math.ceil(FPS / 3)

type RemoveBackgroundFrame = { image: string; mask: string }

/**
 * Posts base64 JPEG frames to Django's background-removal endpoint and returns
 * one base64 mask per frame.
 *
 * Two things are load-bearing:
 *
 * 1. The key is repeated as `frames[]`, because the view reads
 *    `request.POST.getlist("frames[]")` (app/gifs/views.py:1029). Legacy gets
 *    the same shape via append-query inside shared/util/fetch-methods.ts.
 * 2. `version` is omitted rather than defaulted, so Django's own default
 *    ("v3", views.py:1030) applies. The caller passes 'v2' only for the retry.
 *
 * The view catches GrpcException and returns HTTP 200 with an empty frames
 * array — a failure that does not look like one. Legacy discovers this by
 * throwing a TypeError on `stickers[0]` (cut-out/index.tsx:102); this checks
 * for it explicitly. Same error modal, traceable cause. Spec §12.2.
 */
const requestMasks = async (frames: string[], version?: string): Promise<string[]> => {
    const body = new URLSearchParams()
    frames.forEach((frame) => body.append('frames[]', frame))
    if (version) {
        body.append('version', version)
    }

    // Absolute, so the request goes straight to Django. nginx sends everything
    // outside its Next allowlist (nginx.conf:404) to `uwsgi_pass
    // backend_django` via the catch-all at :611, and `/ajax/*` is not in that
    // allowlist — so this needs no Next route in front of it.
    //
    // It briefly went through the upstream proxy while we were chasing a 405.
    // That 405 was Django's own `TooManyFieldsSent`: one `frames[]` field per
    // frame, against `DATA_UPLOAD_MAX_NUMBER_FIELDS` of 1000. The `Server:
    // nginx` header made it look like a routing problem. Batching is the fix;
    // the proxy detour was not.
    const response = await fetch(`${giphyUrl}${REMOVE_BACKGROUND_PATH}`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-CSRFToken': getCookie('csrftoken'),
            'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: body.toString(),
    })

    if (!response.ok) {
        throw new Error(`Background removal failed (${response.status})`)
    }

    // Django's JSON views wrap the body as `{ status, result: { … } }`.
    // Legacy reaches this endpoint through shared/api's `getResults`, which
    // spreads `result` up a level (shared/api/index.ts:88-95) before any
    // consumer sees it — which is why legacy reads `data.frames` off an
    // already-unwrapped object. Calling `response.json()` directly hands us the
    // raw envelope instead, so unwrap it the same way, tolerating a response
    // with no envelope exactly as `getResults` does. Every other api client in
    // this app does the same (labels.ts:14, gif.ts:17).
    const payload = await response.json()
    const unwrapped = payload?.result ?? payload
    const returned: RemoveBackgroundFrame[] = unwrapped?.data?.frames ?? []

    if (returned.length < frames.length) {
        throw new Error(
            `Background removal returned ${returned.length} of ${frames.length} frames — the service is unavailable`
        )
    }

    return returned.map(({ mask }) => mask)
}

/**
 * How the frames will actually be split across requests. Exported so callers
 * sizing their frames can measure the real unit — a single request — rather
 * than guessing at it.
 */
export const backgroundRemovalBatches = (frames: string[]): string[][] => {
    const batches: string[][] = []
    let batch: string[] = []
    let encodedChars = 0

    frames.forEach((frame) => {
        if (
            batch.length > 0 &&
            (batch.length >= FRAMES_PER_REQUEST || encodedChars + frame.length > MAX_ENCODED_CHARS_PER_REQUEST)
        ) {
            batches.push(batch)
            batch = []
            encodedChars = 0
        }

        batch.push(frame)
        encodedChars += frame.length
    })

    if (batch.length > 0) {
        batches.push(batch)
    }

    return batches
}

/**
 * Removes the background from every frame, in order.
 *
 * Batched, because the request used to scale without limit with the source's
 * duration and this is the difference between a GIF working and an MP4 not.
 * `getStickerFromSource` passes no `maxFrames`, so `getVideoFrames` yields
 * `duration * FPS` frames — 300 for a ten-second sticker, each at the video's
 * full resolution — and all of them went into one POST as repeated `frames[]`
 * fields. A GIF contributes its own frame list instead, typically a few dozen
 * small ones, which is why only video sources failed. Legacy has the same
 * unbounded request and the same failure.
 *
 * Batching also keeps each call well inside the Seldon `Predict` deadline
 * (60s, set on the call itself in app/utils/grpc/seldon/client.py — note the
 * `SeldonServiceClient(timeout=5)` in the view sets only the client default,
 * which that call overrides). That is a secondary benefit, not the reason.
 *
 * Sequential rather than concurrent on purpose: this is a shared ML service,
 * and a ten-way fan-out per sticker is not a load pattern to introduce without
 * knowing its capacity. Order is preserved by construction.
 */
export const removeBackground = async (frames: string[], version?: string): Promise<string[]> => {
    const masks: string[] = []

    for (const batch of backgroundRemovalBatches(frames)) {
        masks.push(...(await requestMasks(batch, version)))
    }

    return masks
}
